import { useCallback, useState } from 'react';
import { cursorFor, selectTargetAt, type SelectTarget } from './markupGestures';
import { hitOptionsFor } from './markupHitTest';
import type { MarkupShape, MarkupTool, Point } from './markupShapes';
import { measureLabel } from './textMeasure';

interface UseMarkupHoverArgs {
    tool: MarkupTool;
    shapes: readonly MarkupShape[];
    selected: MarkupShape | null;
    scale: number;
}

export interface MarkupHover {
    cursor: string;
    hoverId: string | null;
    track: (point: Point | null) => void;
}

const NO_TARGET: SelectTarget = { type: 'none' };

function hoveredShapeId(target: SelectTarget): string | null {
    return target.type === 'none' ? null : target.shape.id;
}

// Only the Select tool reacts to what is under the pointer; the others keep a fixed cursor.
export function useMarkupHover({ tool, shapes, selected, scale }: UseMarkupHoverArgs): MarkupHover {
    const [target, setTarget] = useState<SelectTarget>(NO_TARGET);

    const track = useCallback(
        (point: Point | null) => {
            const next =
                point && tool === 'select'
                    ? selectTargetAt(shapes, selected, point, hitOptionsFor(scale), measureLabel)
                    : NO_TARGET;
            setTarget((prev) =>
                cursorFor(tool, prev) === cursorFor(tool, next) &&
                hoveredShapeId(prev) === hoveredShapeId(next)
                    ? prev
                    : next
            );
        },
        [tool, shapes, selected, scale]
    );

    const current = tool === 'select' ? target : NO_TARGET;
    return { cursor: cursorFor(tool, current), hoverId: hoveredShapeId(current), track };
}
